import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API, idr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Receipt, X, CheckCircle2, AlertTriangle, Clock, Calendar, FileSpreadsheet, Settings2, Paperclip, Search, ArrowUpDown, Building2, Lock } from "lucide-react";
import KategoriManager from "./KategoriManager";
import AttachmentPanel from "./AttachmentPanel";
import OcrScanButton from "./OcrScanButton";
import InputRupiah from "../../components/InputRupiah";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useAuth } from "../../context/AuthContext";

/** Nama akun untuk satu baris pembayaran KAS/BANK. Pembayaran BARU menyimpan
 *  `akun_id` (aditif — lihat backend/routers/finance/utang.py) sehingga
 *  namanya bisa ditarik LIVE dari daftar kas/bank (tetap benar walau akun
 *  itu di-rename belakangan). Pembayaran LAMA sebelum perubahan ini tidak
 *  punya `akun_id` — jatuh kembali ke `akun` (nama apa adanya saat dicatat). */
function namaAkunRiwayat(entry, kasBank) {
  if (entry?.akun_id) {
    const found = kasBank.find(a => a.id === entry.akun_id);
    if (found) return found.name;
  }
  return entry?.akun || "-";
}

/** Riwayat pelunasan utang punya DUA jalur berbeda yang TIDAK saling kenal
 *  (lihat catatan `utang.py::utang_bayar_dengan_cn`):
 *   - `payments[]`  : uang tunai keluar dari kas/bank (`utang_bayar`)
 *   - `cn_history[]`: Credit Note memotong TAGIHAN tanpa uang keluar
 *     (`credit_note.py::apply_credit_note_ke_utang`, dipanggil dari sini
 *     maupun dari layar Credit Note)
 *  Kalau riwayat hanya membaca `payments[]`, pelunasan lewat CN hilang dari
 *  layar Utang — makanya digabung di sini jadi satu daftar terurut waktu,
 *  masing-masing ditandai sumbernya supaya tetap bisa dibedakan (bukan
 *  dipalsukan seolah keduanya jenis yang sama). `cn_history` TIDAK punya
 *  field tanggal transaksi terpisah (cuma `at`, instan pencatatan) — dipakai
 *  apa adanya untuk kolom Tanggal baris itu. */
function gabungRiwayatUtang(payments, cnHistory) {
  // Split payment (A2) tetap N BARIS — tiap pecahan adalah uang yang benar-benar
  // keluar dari akun berbeda, dan rekonsiliasi bank mencocokkan per baris. Yang
  // ditambahkan cuma penanda "Split i/n" supaya staf tahu ketiganya satu
  // pembayaran. Entri lama tidak punya `group_id` sama sekali → tanpa penanda,
  // persis seperti sekarang.
  const urutanGrup = {};
  const besarGrup = (payments || []).reduce((m, p) => {
    if (p.group_id) m[p.group_id] = (m[p.group_id] || 0) + 1;
    return m;
  }, {});
  const dariKas = (payments || []).map((p, i) => {
    let split = null;
    if (p.group_id && besarGrup[p.group_id] > 1) {
      urutanGrup[p.group_id] = (urutanGrup[p.group_id] || 0) + 1;
      split = { ke: urutanGrup[p.group_id], dari: besarGrup[p.group_id] };
    }
    return {
      key: p.txn_id || `pay-${i}`, sumber: "kas", tanggal: p.tanggal, nominal: p.nominal,
      akun: p, by: p.by, at: p.at, split,
      // Bayar Sekaligus (brief 22 Sep 2026, H4): satu transfer dibagi ke
      // beberapa tagihan. Tanpa penanda ini, baris Rp 3 juta di tagihan ini
      // tidak bisa dicocokkan ke mutasi bank Rp 10 juta yang sebenarnya.
      sekaligus: !!p.bayar_banyak_id,
    };
  });
  const dariCn = (cnHistory || []).map((c, i) => ({
    key: `${c.credit_note_id || "cn"}-${i}`, sumber: "cn", tanggal: null, nominal: c.nominal,
    cnCode: c.credit_note_code, by: c.by, at: c.at,
  }));
  return [...dariKas, ...dariCn].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
}

// Diekspor untuk Finance › Pelunasan Pembelian (brief 22 Sep 2026, H2): satu
// buku utang, jadi riwayat, status, dan formulir bayarnya dipakai ULANG dari
// sini — bukan disalin (pelajaran helper kembar yang berpisah jalan, CLAUDE.md).
export function RiwayatUtang({ payments, cnHistory, kasBank }) {
  const rows = gabungRiwayatUtang(payments, cnHistory);
  if (rows.length === 0) {
    return <p className="text-xs text-slate-400 py-3 text-center">Belum ada pembayaran tercatat untuk utang ini.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
        <tr>
          <th className="text-left py-1.5 px-2">Tanggal</th>
          <th className="text-right py-1.5 px-2">Nominal</th>
          <th className="text-left py-1.5 px-2">Sumber</th>
          <th className="text-left py-1.5 px-2">Dicatat Oleh</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.key} className="border-b border-slate-100 last:border-0">
            <td className="py-1.5 px-2 text-slate-700">{formatTanggal(r.tanggal || r.at)}</td>
            <td className="py-1.5 px-2 text-right font-mono text-slate-800">{idr(r.nominal)}</td>
            <td className="py-1.5 px-2 text-slate-700">
              {r.sumber === "kas" ? (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 text-[10px] font-medium">
                    {namaAkunRiwayat(r.akun, kasBank)}
                  </span>
                  {r.split && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium"
                      title="Bagian dari satu pembayaran yang dipecah ke beberapa akun">
                      Split {r.split.ke}/{r.split.dari}
                    </span>
                  )}
                  {r.sekaligus && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium"
                      title="Bagian dari satu transfer yang dibagi ke beberapa tagihan supplier ini">
                      Bayar sekaligus
                    </span>
                  )}
                </span>
              ) : (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-medium">
                  Credit Note {r.cnCode || ""}
                </span>
              )}
            </td>
            <td className="py-1.5 px-2 text-slate-600">{r.by || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const FILTER_TABS = [
  { key: "all", label: "Semua" },
  { key: "belum_bayar", label: "Belum Bayar" },
  { key: "jatuh_tempo", label: "Jatuh Tempo" },
  { key: "lunas", label: "Lunas" },
];

/** Warna badge kolom "Umur" (poin 8 brief owner 17 Agu 2026) — kunci bucket
 *  (`belum_jt`/`0_30`/.../`no_jt`) DITENTUKAN backend (`_shared.AGING_BUCKETS_ORDER`
 *  via `list_utang`), di sini murni pemetaan warna tampilan. Palet mengikuti
 *  urutan keparahan yang sama dengan `piutang_aging_buckets`/`utang_aging_buckets`
 *  supaya CEO tidak melihat dua definisi warna umur yang berbeda. */
export const AGING_BADGE = {
  belum_jt: "bg-slate-50 text-slate-500 border-slate-200",
  "0_30":   "bg-emerald-50 text-emerald-700 border-emerald-200",
  "31_60":  "bg-amber-50 text-amber-700 border-amber-200",
  "61_90":  "bg-orange-50 text-orange-700 border-orange-200",
  over_90:  "bg-rose-50 text-rose-700 border-rose-200",
  no_jt:    "bg-slate-50 text-slate-400 border-slate-200",
};

/** Label kolom "Umur": `umur_hari` NEGATIF = belum jatuh tempo (hari
 *  tersisa, ditampilkan "H-n"), POSITIF = sudah lewat tempo sekian hari.
 *  `null` (utang lunas, atau tanpa tanggal jatuh tempo sama sekali) = "-". */
export function labelUmur(u) {
  if (u.umur_hari == null) return u.aging_bucket === "no_jt" ? "Tanpa JT" : "-";
  return u.umur_hari < 0 ? `H-${Math.abs(u.umur_hari)}` : `${u.umur_hari} hari`;
}

const VALID_FILTERS = FILTER_TABS.map(t => t.key);

export default function FinanceUtang() {
  // A3 (brief 4 Sep 2026): tautan ke PO hanya untuk yang punya izin melihat
  // Pembelian — halaman tujuannya memakai modul lain (aturan #3 CLAUDE.md).
  const { can } = useAuth();
  const bolehLihatPO = can("inventory_po", "view");
  // Tombol Bayar memakai kunci yang SAMA dengan backend
  // (`utang.py::pastikan_boleh_melunasi`, brief 22 Sep 2026 H3). Dulu tombolnya
  // tampil untuk siapa pun yang bisa membuka halaman ini, dan akun yang izin
  // ubahnya dicabut di Kelola Pengguna menekan tombol yang pasti 403.
  const bolehBayar = can("finance_utang", "update");
  // A1: vendor terpilih; daftarnya dari server (`stats.suppliers`), bukan dari
  // baris yang sudah dipotong 500.
  const [supplier, setSupplier] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // KENAPA baca query param: dashboard CEO (kartu AP Jatuh Tempo) menautkan ke
  // sini lewat `?filter=jatuh_tempo[&highlight=<id>]` supaya CEO/SPV langsung
  // mendarat di daftar utang yang relevan. Sebelumnya `filter` HANYA hidup di
  // useState lokal dan tidak pernah membaca URL — tautan itu selalu diabaikan
  // dan mendarat di tab "Semua". `highlight` dipakai untuk menyorot & scroll ke
  // baris utang spesifik yang dituju dari kartu KPI.
  const urlFilter = searchParams.get("filter");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_aktif: 0, count: 0 });
  const [filter, setFilter] = useState(VALID_FILTERS.includes(urlFilter) ? urlFilter : "all");
  const [highlightId, setHighlightId] = useState(searchParams.get("highlight") || null);
  const [showForm, setShowForm] = useState(false);
  const [showBayar, setShowBayar] = useState(null);
  const [showKatMgr, setShowKatMgr] = useState(false);
  const [showAtt, setShowAtt] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [showRekapSupplier, setShowRekapSupplier] = useState(false);
  const [kategori, setKategori] = useState([]);
  const [kasBank, setKasBank] = useState([]);
  const rowRefs = useRef({});

  // Pencarian & sort (poin 5, brief owner 19 Agu 2026) — SATU kotak teks yang
  // dicocokkan ke tiga sumbu sekaligus (tanggal/supplier/no PO-DO) di backend
  // (lihat `utang.py::_klausa_cari_utang`), plus kontrol urut. `cariInput`
  // dipisah dari `cari` supaya ketikan tidak memicu fetch di SETIAP huruf —
  // baru dikirim ke server 350ms setelah pengguna berhenti mengetik.
  const [cariInput, setCariInput] = useState("");
  const [cari, setCari] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  // Rentang tanggal utang (brief owner 12 Sep, U2). Isian dipisah dari nilai
  // yang DITERAPKAN: owner minta tombol "Tampilkan" eksplisit, jadi mengetik
  // tanggal tidak boleh langsung memicu permintaan ke server. `dari`/`sampai`
  // itulah yang masuk dependency useEffect.
  const [dariInput, setDariInput] = useState("");
  const [sampaiInput, setSampaiInput] = useState("");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setCari(cariInput.trim()), 350);
    return () => clearTimeout(t);
  }, [cariInput]);
  // Riwayat pembayaran per utang (poin 1) — expand baris, gabungan payments[]
  // (kas/bank) + cn_history[] (Credit Note), lihat `gabungRiwayatUtang`.
  const [expandedId, setExpandedId] = useState(null);

  // Pemicu muat ulang setelah modal (tambah/bayar/kategori/lampiran) ditutup.
  const [reload, setReload] = useState(0);
  const load = () => setReload(n => n + 1);

  // Sinkron ulang kalau URL berubah SETELAH halaman ini sudah ter-mount (mis.
  // navigasi dari link dashboard lain sementara tab utang masih terbuka di
  // riwayat browser — React Router tidak selalu remount komponen yang sama).
  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && VALID_FILTERS.includes(f) && f !== filter) setFilter(f);
    setHighlightId(searchParams.get("highlight") || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectFilter = (key) => {
    setFilter(key);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("filter", key);
      next.delete("highlight"); // ganti tab manual = highlight lama tidak relevan lagi
      return next;
    }, { replace: true });
  };

  // KENAPA BENTUKNYA BEGINI: versi lama menaruh fungsi `load` (yang dibuat
  // ulang tiap render) di dependency useEffect — efek jalan lagi SETIAP
  // render, memicu fetch tanpa henti (layar berkedip), dan respons tab lama
  // yang datang belakangan menimpa tab aktif: utang "Aktif" bisa nongol di
  // tab Lunas. Flag `batal` memastikan hanya respons request TERAKHIR yang
  // boleh menyentuh state.
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const params = { filter };
        if (cari) params.cari = cari;
        if (supplier) params.supplier = supplier;
        if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
        // Disaring di SERVER. Daftarnya dipotong 500 baris dan `total_aktif`/
        // `total_jatuh_tempo` datang dari agregasi server — kalau rentangnya
        // disaring di layar, barisnya menyusut tapi angkanya tidak, dan dua
        // angka di layar yang sama jadi saling membantah.
        if (dari) params.dari = dari;
        if (sampai) params.sampai = sampai;
        const r = await api.get("/finance/utang", { params });
        if (batal) return;
        setItems(r.data.items || []); setStats(r.data);
      } catch { if (!batal) toast.error("Gagal memuat utang"); }
    })();
    return () => { batal = true; };
  }, [filter, reload, cari, sortBy, sortDir, supplier, dari, sampai]);

  // Scroll otomatis ke baris yang ditautkan dari dashboard, begitu datanya sudah masuk.
  useEffect(() => {
    if (highlightId && rowRefs.current[highlightId]) {
      rowRefs.current[highlightId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [items, highlightId]);

  // Opsi kategori & akun kas tidak tergantung tab — cukup dimuat sekali dan
  // saat modal ditutup (kategori bisa berubah dari Kelola Kategori).
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const [k, kb] = await Promise.all([api.get("/finance/utang/kategori-options"), api.get("/finance/accounts/kas-bank")]);
        if (batal) return;
        setKategori(k.data.kategori || []); setKasBank(kb.data || []);
      } catch { /* form tetap terbuka dengan pilihan kosong; daftar utama tidak terganggu */ }
    })();
    return () => { batal = true; };
  }, [reload]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
  const dueSoon = items.filter(u => u.status === "aktif" && u.tanggal_jatuh_tempo && new Date(u.tanggal_jatuh_tempo) <= in30);
  const projeksi = dueSoon.reduce((s, u) => s + ((u.nominal || 0) - (u.nominal_terbayar || 0)), 0);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-utang">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Accounts Payable</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Utang Usaha</h1>
          <p className="text-sm text-slate-600 mt-1">{stats.count} utang · Total {idr(stats.total_aktif)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowRekapSupplier(true)} data-testid="utg-rekap-supplier-btn" className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
            <Building2 className="w-4 h-4" /> Rekap per Supplier
          </button>
          <button onClick={() => setShowKatMgr(true)} data-testid="utg-kat-manage" className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
            <Settings2 className="w-4 h-4" /> Kelola Kategori
          </button>
          <button onClick={() => setShowExport(true)} data-testid="utg-export" className="h-10 px-3 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
          <button onClick={() => setShowForm(true)} data-testid="utg-new" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium">
            <Plus className="w-4 h-4" /> Tambah Utang
          </button>
        </div>
      </div>

      {/* Cash Projection 30d */}
      <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 grid place-items-center shrink-0"><Calendar className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">Proyeksi Kebutuhan Kas 30 Hari</p>
            <p className="font-display text-2xl font-bold text-rose-900 mt-1">{idr(projeksi)}</p>
            <p className="text-xs text-rose-700 mt-1">{dueSoon.length} utang jatuh tempo dalam 30 hari mendatang</p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
        {FILTER_TABS.map(t => (
          <button key={t.key} onClick={() => selectFilter(t.key)} data-testid={`utg-filter-${t.key}`}
            className={`px-4 h-9 rounded-lg text-sm font-medium transition ${filter === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Sortir periode (brief owner 12 Sep, U2): Dari Tanggal, Sampai Tanggal,
          tombol Tampilkan. Menyaring `tanggal` UTANG (bukan jatuh tempo) —
          sama dengan rentang di Export Excel, supaya staf yang mengekspor
          rentang yang sama tidak mendapat isi yang berbeda. */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex flex-wrap items-end gap-3"
           data-testid="utg-periode">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari Tanggal</label>
          <input type="date" value={dariInput} onChange={e => setDariInput(e.target.value)}
            data-testid="utg-dari" max={sampaiInput || undefined}
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai Tanggal</label>
          <input type="date" value={sampaiInput} onChange={e => setSampaiInput(e.target.value)}
            data-testid="utg-sampai" min={dariInput || undefined}
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" />
        </div>
        <button type="button" data-testid="utg-tampilkan"
          onClick={() => { setDari(dariInput); setSampai(sampaiInput); }}
          className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
          Tampilkan
        </button>
        {(dari || sampai) && (
          <button type="button" data-testid="utg-periode-reset"
            onClick={() => { setDariInput(""); setSampaiInput(""); setDari(""); setSampai(""); }}
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-500 hover:bg-slate-50">
            Semua periode
          </button>
        )}
        {(dari || sampai) && (
          <p className="text-[11px] text-slate-500" data-testid="utg-periode-aktif">
            Menampilkan utang bertanggal {dari || "awal"} s/d {sampai || "sekarang"}.
            Angka total di atas ikut mengikuti periode ini.
          </p>
        )}
      </div>

      {/* Pencarian & sort (poin 5) — satu kotak dicocokkan ke tanggal, supplier,
          dan no PO/DO sekaligus di backend (`utang.py::_klausa_cari_utang`). */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={cariInput} onChange={e => setCariInput(e.target.value)}
            placeholder="Cari tanggal, supplier, atau no PO/DO..." data-testid="utg-cari"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
        </div>
        <select value={supplier} onChange={e => setSupplier(e.target.value)} data-testid="utg-supplier"
          title="Saring per vendor (daftar mengikuti tab yang aktif)"
          className="h-10 px-2 rounded-lg border border-slate-200 text-xs bg-white max-w-[230px]">
          <option value="">Semua vendor</option>
          {(stats?.suppliers || []).map(s => <option key={s} value={s}>{s}</option>)}
          {supplier && !(stats?.suppliers || []).includes(supplier) && <option value={supplier}>{supplier}</option>}
        </select>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <ArrowUpDown className="w-3.5 h-3.5" />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="utg-sort-by"
            className="h-9 px-2 rounded-lg border border-slate-200 text-xs bg-white">
            <option value="">Urutan bawaan</option>
            <option value="tanggal">Urut: Tanggal</option>
            <option value="supplier">Urut: Supplier</option>
            <option value="no_dokumen">Urut: No PO/DO</option>
          </select>
          {sortBy && (
            <select value={sortDir} onChange={e => setSortDir(e.target.value)} data-testid="utg-sort-dir"
              className="h-9 px-2 rounded-lg border border-slate-200 text-xs bg-white">
              <option value="asc">A-Z / Terlama</option>
              <option value="desc">Z-A / Terbaru</option>
            </select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {/* U1 (brief 12 Sep): Kode dan Tanggal dipisah jadi dua kolom. */}
                <th className="px-4 py-2.5 text-left">Kode</th>
                <th className="px-4 py-2.5 text-left">Tanggal</th>
                <th className="px-4 py-2.5 text-left">Supplier</th>
                <th className="px-4 py-2.5 text-left">Kategori</th>
                <th className="px-4 py-2.5 text-left">Jatuh Tempo</th>
                <th className="px-4 py-2.5 text-center">Umur</th>
                <th className="px-4 py-2.5 text-right">Nominal</th>
                <th className="px-4 py-2.5 text-right">Sisa</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={10} className="p-10 text-center text-sm text-slate-400">Belum ada utang di filter ini</td></tr>
              ) : items.map(u => {
                const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
                const attCount = (u.attachments || []).length;
                const isHighlighted = u.id === highlightId;
                const isExpanded = expandedId === u.id;
                const riwayatCount = (u.payments || []).length + (u.cn_history || []).length;
                return (
                  <Fragment key={u.id}>
                    <tr
                      ref={el => { if (el) rowRefs.current[u.id] = el; }}
                      data-testid={isHighlighted ? "utg-row-highlighted" : undefined}
                      className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${isHighlighted ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{u.code}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                        {u.tanggal ? formatTanggal(u.tanggal) : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{u.supplier_nama}</p>
                        {/* Dulu membaca `u.no_invoice` yang tidak pernah ada di dokumen utang —
                            barisnya selalu kosong. Nomor PO/Invoice sekarang ikut di keterangan. */}
                        {u.keterangan && <p className="text-[10px] text-slate-500 truncate max-w-[220px]" title={u.keterangan}>{u.keterangan}</p>}
                        {/* A3: nomor PO/DO dari tautan id (bukan diurai dari keterangan) —
                            klik membuka pratinjau PO untuk mencocokkan tagihan. */}
                        {(u.po_code || u.do_code) && (
                          <p className="text-[10px] mt-0.5 space-x-1.5">
                            {u.po_code && (bolehLihatPO
                              ? <Link to={`/inventory/pembelian?po=${u.source_po_id}`} data-testid={`utg-po-${u.id}`}
                                      className="font-mono text-rose-700 underline underline-offset-2">{u.po_code}</Link>
                              : <span className="font-mono text-slate-600">{u.po_code}</span>)}
                            {u.do_code && <span className="font-mono text-slate-500">DO {u.do_code}</span>}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">{u.kategori}</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{u.tanggal_jatuh_tempo || "-"}</td>
                      <td className="px-4 py-3 text-center">
                        <span data-testid={`utg-umur-${u.id}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${AGING_BADGE[u.aging_bucket] || AGING_BADGE.no_jt}`}>
                          {labelUmur(u)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-800">{idr(u.nominal)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(sisa)}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusPill s={u.status_tampilan || u.status} />
                        {/* Progres pembayaran (poin 9) — hanya bermakna selama utang masih
                            aktif (mencakup 0% "belum dibayar" dan >0% "terbayar sebagian");
                            lunas/pending/ditolak tidak butuh progress bar. */}
                        {u.status === "aktif" && (
                          <div className="mt-1 w-16 mx-auto" data-testid={`utg-progres-${u.id}`}>
                            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-1 bg-rose-400" style={{ width: `${u.persen_terbayar || 0}%` }} />
                            </div>
                            <p className="text-[9px] text-slate-400 mt-0.5">{(u.persen_terbayar || 0).toFixed(0)}%</p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => setShowAtt(u)} data-testid={`utg-att-${u.id}`} title="Kelola Lampiran" className="mr-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-700">
                          <Paperclip className="w-3.5 h-3.5" /> {attCount > 0 && attCount}
                        </button>
                        <button onClick={() => setExpandedId(isExpanded ? null : u.id)} data-testid={`utg-riwayat-toggle-${u.id}`}
                          title="Riwayat Pembayaran" className="mr-2 text-xs text-slate-500 hover:text-rose-700">
                          Riwayat{riwayatCount > 0 && ` (${riwayatCount})`}
                        </button>
                        {u.status === "aktif" && bolehBayar && (
                          <button onClick={() => setShowBayar(u)} data-testid={`utg-bayar-${u.id}`} className="text-xs font-medium text-rose-700 hover:underline">Bayar</button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr data-testid={`utg-riwayat-row-${u.id}`}>
                        <td colSpan={10} className="px-4 py-3 bg-slate-50/60 border-b border-slate-100">
                          <RiwayatUtang payments={u.payments} cnHistory={u.cn_history} kasBank={kasBank} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <FormUtang kategori={kategori} onClose={() => { setShowForm(false); load(); }} />}
      {showBayar && <FormBayarUtang utang={showBayar} kasBank={kasBank} onClose={() => { setShowBayar(null); load(); }} />}
      {showKatMgr && <KategoriManager jenis="utang" title="Kelola Kategori Utang" onClose={() => { setShowKatMgr(false); load(); }} />}
      {showAtt && <AttachmentPanel module="utang" objId={showAtt.id} objLabel={`${showAtt.code} · ${showAtt.supplier_nama}`} onClose={() => { setShowAtt(null); load(); }} />}
      {showExport && <UtangExportDialog onClose={() => setShowExport(false)} />}
      {showRekapSupplier && <RekapSupplierModal onClose={() => setShowRekapSupplier(false)} />}
    </div>
  );
}

function UtangExportDialog({ onClose }) {
  // `hariIniISO()`, BUKAN `new Date().toISOString().slice(0,10)` — yang
  // terakhir selalu UTC dan memberi tanggal KEMARIN bagi pengguna WIB antara
  // 00:00-07:00 (lihat frontend/src/lib/tanggal.js).
  const today = hariIniISO();
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [busy, setBusy] = useState(false);
  const doExport = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/finance/utang/export-xlsx?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `utang-${start}_sd_${end}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel berhasil di-download 📊"); onClose();
    } catch { toast.error("Gagal export"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-rose-600" /> Export Utang ke Excel</h3>
        <p className="text-xs text-slate-500 mb-4">3 sheet by status: Belum Bayar · Jatuh Tempo · Lunas + Ringkasan</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Mulai</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="utg-exp-start" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-rose-500 outline-none" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Akhir</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="utg-exp-end" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-rose-500 outline-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={doExport} disabled={busy} data-testid="utg-exp-submit" className="flex-1 h-10 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium disabled:opacity-50">{busy ? "Generating..." : "Download"}</button>
        </div>
      </div>
    </div>
  );
}

/** Rekap per supplier LANGSUNG di halaman Utang Usaha (poin 5, brief owner
 *  19 Agu 2026) — beda dari kartu ringkasan di halaman Supplier
 *  (`SupplierDetail`/`SupplierList`, digerbangi `inventory_po.view`): sumber
 *  datanya `GET /finance/utang/rekap-supplier`, gerbangnya SAMA dengan
 *  `GET /finance/utang` sendiri, jadi staf yang cuma pegang izin Finance >
 *  Utang tetap bisa membukanya (aturan #3 CLAUDE.md — jangan tampilkan
 *  tombol yang ujungnya 403). Menampilkan jumlah_manual/jumlah_dari_po per
 *  baris supaya kelihatan kalau utang manual & dari PO memang sama-sama
 *  terhitung, bukan cuma diklaim di komentar kode. */
function RekapSupplierModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let hidup = true;
    api.get("/finance/utang/rekap-supplier").then(r => { if (hidup) setData(r.data); })
      .catch(() => { if (hidup) toast.error("Gagal memuat rekap supplier"); })
      .finally(() => { if (hidup) setLoading(false); });
    return () => { hidup = false; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="utg-rekap-supplier-modal"
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-rose-600" /> Rekap Utang per Supplier
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        {data && (
          <p className="text-xs text-slate-500 mb-3">
            {data.count} supplier · Total sisa hutang aktif {idr(data.total_sisa)}
          </p>
        )}
        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Memuat...</p>
          ) : !data || data.items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Belum ada utang tercatat</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200 sticky top-0 bg-white">
                <tr>
                  <th className="text-left py-1.5 px-2">Supplier</th>
                  <th className="text-center py-1.5 px-2">Tagihan</th>
                  <th className="text-center py-1.5 px-2">Asal</th>
                  <th className="text-right py-1.5 px-2">Sisa Hutang</th>
                  <th className="text-left py-1.5 px-2">JT Terdekat</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(it => (
                  <tr key={it.supplier_nama} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 px-2 font-medium text-slate-900">{it.supplier_nama}</td>
                    <td className="py-1.5 px-2 text-center text-slate-600">{it.jumlah_tagihan}</td>
                    <td className="py-1.5 px-2 text-center text-slate-500">
                      {it.jumlah_dari_po > 0 && <span title="Dari penerimaan PO">{it.jumlah_dari_po} PO</span>}
                      {it.jumlah_dari_po > 0 && it.jumlah_manual > 0 && " · "}
                      {it.jumlah_manual > 0 && <span title="Input manual">{it.jumlah_manual} manual</span>}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono font-semibold text-slate-900">{idr(it.total_sisa)}</td>
                    <td className="py-1.5 px-2 text-slate-600">{it.jatuh_tempo_terdekat || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function StatusPill({ s }) {
  const map = {
    aktif:             { c: "bg-sky-50 text-sky-700 border-sky-200", i: Clock, l: "Aktif" },
    // Field turunan (poin 9 brief owner 17 Agu 2026) — status TERSIMPAN di
    // dokumen tetap "aktif" (lihat `_shared._status_tampilan_ap_ar`), ini
    // cuma label tampilan tambahan untuk utang yang sudah dibayar sebagian.
    terbayar_parsial:  { c: "bg-amber-50 text-amber-700 border-amber-200", i: Clock, l: "Terbayar Parsial" },
    lunas:             { c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: CheckCircle2, l: "Lunas" },
    pending_approval:  { c: "bg-amber-50 text-amber-800 border-amber-200", i: AlertTriangle, l: "Pending SPV" },
    rejected:          { c: "bg-rose-50 text-rose-700 border-rose-200", i: X, l: "Ditolak" },
  };
  const it = map[s] || map.aktif; const Icon = it.i;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${it.c}`}><Icon className="w-3 h-3" /> {it.l}</span>;
}

// Poin 6 (brief owner 19 Agu 2026): modul & target_id KONSTAN dipakai gerbang
// PIN "Tambah Utang" (`utang.py::PIN_MODUL_UTANG_MANUAL/PIN_TARGET_UTANG_MANUAL`)
// — belum ada dokumen saat PIN diajukan (beda dari pola hapus yang target_id-nya
// id dokumen yang SUDAH ada), tapi PIN tetap sekali pakai per pengajuan jadi
// konstanta ini tidak membuka celah replay.
// Cermin `utang.py::MAKS_PECAHAN_BAYAR`. Hanya untuk mematikan tombol "+ Tambah
// akun"; penegakannya tetap di backend (400 kalau lebih), jadi kalau angkanya
// pernah berbeda yang terjadi cuma tombol mati kecepatan/kelambatan — bukan
// pembayaran yang lolos.
const MAKS_PECAHAN_BAYAR = 10;

const PIN_MODUL_UTANG_MANUAL = "finance_utang_manual";
const PIN_TARGET_UTANG_MANUAL = "baru";

function FormUtang({ kategori, onClose }) {
  // `hariIniISO()` — default tanggal utang BARU langsung memengaruhi tanggal
  // jurnal; `toISOString()` (UTC) memundurkan sehari untuk pengguna WIB
  // sebelum jam 07:00.
  const [form, setForm] = useState({ tanggal: hariIniISO(), supplier_nama: "", no_invoice: "", nominal: "", kategori: "", tanggal_jatuh_tempo: "", brand: "", keterangan: "" });
  const [busy, setBusy] = useState(false);
  // PIN persetujuan (poin 6) — dialog terpisah, MENIRU PERSIS alur PIN hapus
  // yang sudah ada di `Sales.jsx`/`Acquisitions.jsx` (428 -> ajukan otomatis
  // -> minta pengguna masukkan PIN -> kirim ulang dengan PIN itu), bukan
  // sistem baru.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error}

  const handleOcr = (d) => {
    setForm(f => ({
      ...f,
      tanggal: d.tanggal || f.tanggal,
      nominal: d.nominal != null ? String(d.nominal) : f.nominal,
      supplier_nama: d.supplier_nama || f.supplier_nama,
      no_invoice: d.no_invoice || f.no_invoice,
      keterangan: d.keterangan || f.keterangan,
    }));
  };

  const doCreateUtang = (pin) => {
    // UtangReq di backend tidak punya field `no_invoice`, jadi nomor PO/Invoice yang diketik
    // (atau hasil scan OCR) selama ini dibuang Pydantic tanpa peringatan — user mengira
    // nomornya tersimpan padahal hilang. Nomor itu tetap berharga saat mencocokkan tagihan
    // supplier, jadi dititipkan ke `keterangan` yang memang diterima & disimpan backend.
    const { no_invoice, ...sisa } = form;
    const keterangan = [no_invoice.trim() && `No PO/Invoice: ${no_invoice.trim()}`, form.keterangan.trim()]
      .filter(Boolean).join(" — ");
    const payload = { ...sisa, keterangan, nominal: Number(form.nominal) };
    if (pin) payload.pin = pin;
    return api.post("/finance/utang", payload);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await doCreateUtang();
      toast.success(r.data.message); onClose();
    } catch (e) {
      if (e?.response?.status === 428) {
        // Rawan fraud (brief owner) — server minta PIN. Ajukan otomatis
        // lewat mekanisme PIN yang SUDAH ADA (dipakai hapus nota/akuisisi),
        // lalu buka dialog supaya staf tinggal menempelkan kode yang
        // diberikan Manager/CEO (pemegang izin Pusat Approval).
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: PIN_MODUL_UTANG_MANUAL, target_id: PIN_TARGET_UTANG_MANUAL,
            ringkasan: `Tambah utang manual — ${form.supplier_nama || "(tanpa nama)"} Rp ${Number(form.nominal || 0).toLocaleString("id-ID")}`,
            // PIN diikat ke nominal + supplier (backend `_konteks_hash`) — kirim
            // persis nilai yang akan dipakai saat submit-dengan-PIN di bawah,
            // supaya PIN yang diterbitkan sungguh hanya sah untuk transaksi ini.
            nominal: Number(form.nominal || 0), supplier_nama: form.supplier_nama,
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan PIN persetujuan");
          setBusy(false);
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "" });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal simpan");
      }
    } finally { setBusy(false); }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      const r = await doCreateUtang(pinDialog.pin);
      toast.success(r.data.message);
      setPinDialog(null);
      onClose();
    } catch (e) {
      if (e?.response?.status === 403) {
        // Pakai detail dari backend: sejak PIN diikat ke nominal+supplier
        // (konteks_hash, routers/aktivitas.py), 403 punya sebab KETIGA —
        // "diterbitkan untuk nominal/supplier yang berbeda" — yang tidak
        // tercakup teks tetap lama dan membuat staf mengulang PIN yang sama.
        setPinDialog(d => ({ ...d, submitting: false,
          error: e?.response?.data?.detail || "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal simpan");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // `dangerous` — form Utang Baru punya 7 field termasuk nominal yang diketik
  // staf sendiri; kehilangannya lewat klik tak sengaja di luar/Escape berarti
  // mengetik ulang semua, termasuk hasil scan OCR.
  const guard = useModalGuard({
    onClose, onSubmit: submit, busy,
    disabled: busy || !form.supplier_nama || !form.nominal || !form.kategori,
    dangerous: true,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="utg-form-modal" className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Receipt className="w-5 h-5 text-rose-600" /> Utang Baru</h3>
          <OcrScanButton mode="utang" onExtracted={handleOcr} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FieldLite label="Tanggal"><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} className="in" /></FieldLite>
          <FieldLite label="Jatuh Tempo"><input type="date" value={form.tanggal_jatuh_tempo} onChange={e => setForm({ ...form, tanggal_jatuh_tempo: e.target.value })} className="in" /></FieldLite>
          <FieldLite label="Nama Supplier" col2><input value={form.supplier_nama} onChange={e => setForm({ ...form, supplier_nama: e.target.value })} className="in" data-testid="utg-form-nama" /></FieldLite>
          <FieldLite label="No PO/Invoice (disimpan di keterangan)" col2><input value={form.no_invoice} onChange={e => setForm({ ...form, no_invoice: e.target.value })} className="in" data-testid="utg-form-inv" /></FieldLite>
          <FieldLite label="Kategori"><select value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })} className="in" data-testid="utg-form-kat"><option value="">Pilih...</option>{kategori.map(k => <option key={k} value={k}>{k}</option>)}</select></FieldLite>
          <FieldLite label="Nominal"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="in" testid="utg-form-nom" /></FieldLite>
          <FieldLite label="Keterangan" col2><textarea rows={2} value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="in" /></FieldLite>
        </div>
        <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1">
          <Lock className="w-3 h-3" /> Utang manual butuh PIN persetujuan dari Manager/CEO (rawan fraud).
        </p>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="utg-form-guard" /></div>
        )}
        <div className="flex gap-2 mt-3">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !form.supplier_nama || !form.nominal || !form.kategori} data-testid="utg-form-submit" className="flex-1 h-10 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #E11D48; box-shadow: 0 0 0 3px rgba(225,29,72,0.1); } textarea.in { height: auto; padding: 10px 12px; }`}</style>
      </div>

      {/* PIN PERSETUJUAN TAMBAH UTANG (poin 6) — layer TERPISAH (z-[70], di
          atas modal form yang z-50) supaya form tidak tertutup/hilang datanya
          selagi staf menunggu PIN dari Manager/CEO. */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={e => { e.stopPropagation(); setPinDialog(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="utg-pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Tambah Utang
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Tambah utang manual butuh persetujuan. Minta PIN ke Manager/CEO (pemegang izin
                Pusat Approval — mereka bisa melihatnya di menu Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="utg-pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="utg-pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Satu-satunya formulir bayar SATU tagihan — dipakai halaman Utang DAN
// Finance › Pelunasan Pembelian (brief 22 Sep 2026, H2 "mesin bayar yang sama").
// Gerbang tombol pembukanya tanggung jawab pemanggil: can("finance_utang","update").
export function FormBayarUtang({ utang, kasBank, onClose }) {
  const sisa = (utang.nominal || 0) - (utang.nominal_terbayar || 0);
  // `hariIniISO()` — default tanggal PEMBAYARAN utang menyentuh uang & tanggal
  // jurnal langsung; `toISOString()` (UTC) memundurkan sehari untuk pengguna
  // WIB sebelum jam 07:00.
  const [form, setForm] = useState({ tanggal: hariIniISO(), nominal: sisa, sumber_akun_id: "", keterangan: "" });
  // Split payment (A2, brief owner 4 Sep 2026). Mati = formulir lama persis,
  // dan permintaannya pun tidak membawa field `sumber` sama sekali.
  const [split, setSplit] = useState(false);
  const [baris, setBaris] = useState([{ akun_id: "", nominal: 0 }, { akun_id: "", nominal: 0 }]);
  const [busy, setBusy] = useState(false);

  const totalBaris = baris.reduce((s, r) => s + (Number(r.nominal) || 0), 0);
  const selisih = Math.round((Number(form.nominal) || 0) - totalBaris);
  const akunKembar = (() => {
    const dipakai = baris.map(r => r.akun_id).filter(Boolean);
    return new Set(dipakai).size !== dipakai.length;
  })();
  // Gerbang yang SAMA dengan `normalisasi_pecahan` di backend — supaya tombol
  // Bayar tidak pernah berakhir 400. Backend tetap penjaga sesungguhnya.
  const belumSiap = busy || !form.nominal || (split
    ? (baris.some(r => !r.akun_id || !(Number(r.nominal) > 0)) || selisih !== 0 || akunKembar)
    : !form.sumber_akun_id);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = split
        ? { tanggal: form.tanggal, keterangan: form.keterangan, nominal: Number(form.nominal),
            sumber: baris.map(r => ({ akun_id: r.akun_id, nominal: Number(r.nominal) })) }
        : { ...form, nominal: Number(form.nominal) };
      const r = await api.post(`/finance/utang/${utang.id}/bayar`, payload);
      toast.success(r.data.message); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal bayar"); }
    finally { setBusy(false); }
  };
  // `dangerous` — nominal pembayaran diketik/dipilih staf sendiri (default
  // dari sisa, tapi bisa diubah), plus pilihan akun sumber dana.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: belumSiap, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="utg-bayar-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-bold text-lg mb-1">Bayar Utang</h3>
        <p className="text-sm text-slate-600 mb-4">
          {utang.supplier_nama} · Sisa {idr(sisa)}
          {/* Nomor PO/DO supaya staf yakin tagihan mana yang sedang dilunasi. */}
          {(utang.po_code || utang.do_code) && (
            <span className="block text-[11px] text-slate-500 font-mono" data-testid="utg-bayar-dok">
              {[utang.po_code, utang.do_code && `DO ${utang.do_code}`].filter(Boolean).join(" · ")}
            </span>
          )}
        </p>
        <div className="space-y-3">
          <FieldLite label="Tanggal"><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} className="in" /></FieldLite>
          <FieldLite label="Nominal Bayar"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="in" testid="utg-bayar-nom" /></FieldLite>

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={split} data-testid="utg-bayar-split-toggle"
              onChange={e => setSplit(e.target.checked)} />
            Bayar dari beberapa akun (split payment)
          </label>

          {!split ? (
            <FieldLite label="Dari Akun"><select value={form.sumber_akun_id} onChange={e => setForm({ ...form, sumber_akun_id: e.target.value })} className="in" data-testid="utg-bayar-akun"><option value="">Pilih...</option>{kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></FieldLite>
          ) : (
            <div className="space-y-2" data-testid="utg-bayar-split">
              {baris.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select value={r.akun_id} data-testid={`utg-split-akun-${i}`}
                    onChange={e => setBaris(baris.map((x, j) => j === i ? { ...x, akun_id: e.target.value } : x))}
                    className="in flex-1"><option value="">Pilih akun...</option>
                    {kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <div className="w-36">
                    <InputRupiah value={r.nominal} testid={`utg-split-nom-${i}`}
                      onChange={n => setBaris(baris.map((x, j) => j === i ? { ...x, nominal: n } : x))}
                      className="in" />
                  </div>
                  <button type="button" disabled={baris.length <= 2}
                    onClick={() => setBaris(baris.filter((_, j) => j !== i))}
                    data-testid={`utg-split-hapus-${i}`}
                    className="h-10 w-9 shrink-0 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed">×</button>
                </div>
              ))}
              <button type="button" onClick={() => setBaris([...baris, { akun_id: "", nominal: 0 }])}
                disabled={baris.length >= MAKS_PECAHAN_BAYAR} data-testid="utg-split-tambah"
                className="text-xs font-medium text-rose-700 hover:text-rose-800 disabled:opacity-40">
                + Tambah akun
              </button>
              {/* Selisih ditampilkan terus-menerus, bukan cuma saat submit:
                  backend MENOLAK kalau jumlah pecahan tidak sama dengan nominal
                  bayar, dan staf harus tahu itu sebelum menekan tombolnya. */}
              <p className={`text-xs ${selisih === 0 ? "text-emerald-700" : "text-amber-700"}`}
                 data-testid="utg-split-selisih">
                Total pecahan {idr(totalBaris)} · {selisih === 0 ? "pas ✓" : `selisih ${idr(selisih)}`}
              </p>
              {akunKembar && (
                <p className="text-xs text-rose-700" data-testid="utg-split-kembar">
                  Ada akun yang dipilih lebih dari sekali — gabungkan jadi satu baris.
                </p>
              )}
            </div>
          )}
          <FieldLite label="Keterangan"><input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="in" /></FieldLite>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="utg-bayar-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={belumSiap} data-testid="utg-bayar-submit" className="flex-1 h-10 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Bayar"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #E11D48; box-shadow: 0 0 0 3px rgba(225,29,72,0.1); }`}</style>
      </div>
    </div>
  );
}

export function FieldLite({ label, children, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
